import { MessageBarType, Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { BaseDialog, Button, buttonStylesPrimary, useToast } from '@h2oai/ui-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { hasOp } from '../../aiem/engine/conditions';
import { FilterCondition, useEngine } from '../../aiem/engine/hooks';
import { AIEMOpType, AIEngine, EngineState } from '../../aiem/engine/types';
import { Engine, Engine_Type } from '../../aiem/gen/ai/h2o/engine/v1/engine_pb';
import { getIdFromName } from '../../aiem/utils';
import {
  restrictionTooltipMessage,
  usePermissions,
} from '../../authz/providers/PermissionsProvider/PermissionsProvider';
import ListPage from '../../components/ListPages/ListPage';
import { useWorkspaces } from '../../pages/Orchestrator/WorkspaceProvider';
import { stackStylesPage } from '../../themes/themes';
import { useCloudPlatformDiscovery, useDebouncedCallback } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import FilterPanel, { FilterPanelInput } from '../FilterPanel/FilterPanel';
import { AIEMConfirmDialog } from './components/AIEMConfirmDialog/AIEMConfirmDialog';
import { AIEMMigrateCreator } from './components/AIEMMigrateCreator/AIEMMigrateCreator';
import { AIEMPanelWrapper } from './components/AIEMPanel/AIEMPanelWrapper';
import { AIEMUpgradeDialog } from './components/AIEMUpgradeDialog/AIEMUpgradeDialog';
import { EngineList } from './components/EngineList/EngineList';
import { EngineResizeDialog } from './components/EngineResizeDialog/EngineResizeDialog';
import EngineVersionFilter from './components/EngineVersionFilter';
import EngineStateFilter from './components/filters/EngineStateFilter';
import { aiEnginesPageUserActions, EngineFilterType } from './constants';
import { useGetEngines } from './hooks/useGetEngines';

type AIEMPanelState = {
  op: AIEMOpType | null;
  isOpen: boolean;
  engine?: AIEngine;
};

type ConfirmDialogType = {
  op: AIEMOpType.pause | AIEMOpType.delete | AIEMOpType.resume | AIEMOpType.terminate;
};

interface AIEnginesPageProps {
  title: string;
  fetchEngines: (filter?: FilterCondition[]) => Promise<Engine[] | undefined>;
  admin?: boolean;
  workspace?: string;
}

function AIEnginesPage(props: AIEnginesPageProps) {
  const { fetchEngines, admin, workspace } = props;
  const [
    canListAll,
    canDAIList,
    canDAICreate,
    canDAIResume,
    canDAIPause,
    canDAIDelete,
    canNotebookList,
    canNotebookCreate,
    canNotebookResume,
    canNotebookPause,
    canNotebookDelete,
    canH2OList,
    canH2OCreate,
    canH2OTerminate,
    canH2ODelete,
    permissionsLoading,
  ] = usePermissions(aiEnginesPageUserActions, workspace);
  const canListSome = canDAIList || canH2OList || canNotebookList;
  const canCreateSome = canDAICreate || canH2OCreate || canNotebookCreate;
  const canResumeSome = canDAIResume || canNotebookResume;
  const canPauseSome = canDAIPause || canNotebookPause;
  const canDeleteSome = canDAIDelete || canH2ODelete || canNotebookDelete;
  // API returns false for every permission when fetching for all workspaces ("workspaces/-")
  // TODO: Uncomment when fixed
  // const canFetchEngines = (!canListAll && admin) || !canListSome,
  const canFetchEngines = admin || canListSome,
    { addToast } = useToast(),
    history = useHistory(),
    location = useLocation(),
    platformDiscovery = useCloudPlatformDiscovery(),
    { EngineStateMap, opOnEngine } = useEngine(),
    { activeWorkspace } = useWorkspaces(),
    [filterPanelOpen, { setTrue: openFilterPanel, setFalse: dismissFilterPanel }] = useBoolean(false),
    [selectedEngineIds, setSelectedEngineIds] = useState<string[]>([]),
    engines = useGetEngines(fetchEngines, { refetchInterval: 5_000 }, !canFetchEngines, workspace),
    lastErrorRef = useRef<unknown>(null),
    [panel, setPanel] = useState<AIEMPanelState>({ op: AIEMOpType.create, isOpen: false }),
    [dialog, setDialog] = useState<ConfirmDialogType | null>(null),
    [upgrade, setUpgrade] = useState<AIEMOpType.upgrade | null>(null),
    [engineResize, setEngineResize] = useState<AIEMOpType.resize | null>(null),
    [migrateCreator, setMigrateCreator] = useState<AIEMOpType.migrateCreator | null>(null),
    [actionEngine, setActionEngine] = useState<AIEngine | undefined>(),
    [bulkActionEngine, setBulkActionEngine] = useState<AIEngine[]>([]),
    [deprecatedEngine, setDeprecatedEngine] = useState<AIEngine | null>(null),
    selectedEnginesContainsDeleted = useMemo(() => {
      if (!engines.data) return false;

      const deletedEngineIds = new Set(
        engines.data.filter((engine) => engine.deletedVersion).map((engine) => engine.uid)
      );

      return selectedEngineIds.some((uid) => deletedEngineIds.has(uid));
    }, [selectedEngineIds, engines.data]),
    readLogs = useCallback(
      ({ name }: AIEngine) => {
        history.push(`/logs/?name=${name}`);
      },
      [history]
    ),
    openLegacyEngineLogs = useCallback(
      ({ name, type }: AIEngine) => {
        const id = `${getIdFromName(name)}`;
        history.push(`${location.pathname}/log/${type}/${id}`);
      },
      [history, location]
    ),
    successToast = useCallback(
      (displayName: string, verb?: string) => {
        addToast({
          messageBarType: MessageBarType.success,
          message: `Engine "${displayName}" ${verb} successfully.`,
        });
      },
      [addToast]
    ),
    bulkSuccessToast = useCallback(
      (verb: string, itemCount: number) => {
        const mainTitle = `${String(itemCount)} ${itemCount > 1 ? 'items' : 'item'} ${verb} successfully.`;
        addToast({
          messageBarType: MessageBarType.success,
          message: mainTitle,
        });
      },
      [selectedEngineIds, addToast]
    ),
    editEngine = useCallback(
      async (engine: AIEngine) => {
        const freshEngine = await opOnEngine(engine, AIEMOpType.get, undefined, undefined),
          { engineType } = engine;
        setPanel({
          op: AIEMOpType.edit,
          isOpen: !!freshEngine,
          engine: freshEngine ? ({ ...freshEngine, engineType } as AIEngine) : engine,
        });
      },
      [opOnEngine]
    ),
    viewEngine = useCallback(
      async (engine: AIEngine) => {
        const { engineType } = engine;
        const freshEngine = await opOnEngine(engine, AIEMOpType.get, undefined, undefined);
        setPanel({
          op: AIEMOpType.view,
          isOpen: !!freshEngine,
          engine: freshEngine ? ({ ...freshEngine, engineType } as AIEngine) : engine,
        });
      },
      [opOnEngine]
    ),
    pauseConfirmedEngine = useCallback(
      async (engine: AIEngine) => {
        const res = await opOnEngine(engine, AIEMOpType.pause, undefined, undefined);
        setDialog(null);
        if (res) successToast(String(engine.displayName), 'is pausing');
      },
      [successToast, opOnEngine, setDialog]
    ),
    terminateConfirmedEngine = useCallback(
      async (engine: AIEngine) => {
        const res = await opOnEngine(engine, AIEMOpType.terminate, undefined, undefined);
        setDialog(null);
        if (res) successToast(String(engine.displayName), 'is terminating');
      },
      [successToast, opOnEngine, setDialog]
    ),
    deleteConfirmedEngine = useCallback(
      async (engine: AIEngine) => {
        const res = await opOnEngine(engine, AIEMOpType.delete, undefined, undefined);
        setDialog(null);
        if (res) successToast(String(engine.displayName), 'is deleting');
      },
      [successToast, opOnEngine]
    ),
    actionEngineErrorMessage = (actionName = 'Action') => {
      addToast({
        messageBarType: MessageBarType.severeWarning,
        message: `${actionName} can't be performed`,
        title: `Can't load engine data.`,
      });
    },
    onConfirmDialog = useCallback(async () => {
      if (!actionEngine?.op) {
        actionEngineErrorMessage();
        return;
      }
      const { op } = actionEngine;
      if (op === AIEMOpType.pause) await pauseConfirmedEngine(actionEngine as AIEngine);
      if (op === AIEMOpType.delete) await deleteConfirmedEngine(actionEngine as AIEngine);
      if (op === AIEMOpType.terminate) await terminateConfirmedEngine(actionEngine as AIEngine);
      engines.refetch();
    }, [actionEngine, addToast, engines.refetch, pauseConfirmedEngine, deleteConfirmedEngine]),
    onConfirmBulkDialog = useCallback(async () => {
      const { op } = dialog || {};
      if (!bulkActionEngine || !dialog || !dialog.op) {
        actionEngineErrorMessage();
        return;
      }
      if (op) {
        setDialog(null);
        const actionWaitList: Promise<any>[] = [];
        let cantResumeH2OEngineCounter = 0;
        try {
          selectedEngineIds.forEach(async (uid) => {
            const engine = engines.data?.find((e) => e.uid === uid);
            const { type, state } = engine || {};

            if (type && state && (op === AIEMOpType.delete || hasOp(op, type, state))) {
              const actionEngine = { ...engine, engineType: type } as AIEngine;
              actionWaitList.push(opOnEngine(actionEngine, op, undefined, undefined));
            } else if (op === AIEMOpType.resume && type === Engine_Type.H2O) {
              cantResumeH2OEngineCounter++;
            }
          });

          const res = await Promise.all(actionWaitList);
          if (res && dialog.op === AIEMOpType.pause) bulkSuccessToast('paused', actionWaitList.length);
          if (res && dialog.op === AIEMOpType.terminate) bulkSuccessToast('terminated', actionWaitList.length);
          if (res && dialog.op === AIEMOpType.delete) bulkSuccessToast('deleted', actionWaitList.length);
          if (res && dialog.op === AIEMOpType.resume) bulkSuccessToast('resumed', actionWaitList.length);
          if (cantResumeH2OEngineCounter) {
            addToast({
              messageBarType: MessageBarType.warning,
              message: `Can't resume ${cantResumeH2OEngineCounter} H2O engine${
                cantResumeH2OEngineCounter > 1 ? 's' : ''
              }`,
            });
          }
        } catch (error) {
          actionEngineErrorMessage();
        }

        engines.refetch();

        setBulkActionEngine([]);
      }
    }, [dialog?.op, engines.refetch, pauseConfirmedEngine, deleteConfirmedEngine]),
    onConfirmUpgradeDialog = useCallback(
      async (newVersion: string) => {
        if (!actionEngine?.op) {
          actionEngineErrorMessage('Upgrade');
          return;
        }
        actionEngine.engineNewVersion = newVersion;
        const res = await opOnEngine(actionEngine as AIEngine, AIEMOpType.upgrade, undefined, undefined);
        setUpgrade(null);
        if (res) successToast(String(actionEngine.displayName), 'was upgraded');
      },
      [actionEngine, addToast, opOnEngine]
    ),
    onConfirmResizeDialog = useCallback(
      async (newSize: string) => {
        if (!actionEngine?.op) {
          actionEngineErrorMessage('Resize');
          return;
        }
        actionEngine.newSize = newSize;
        const res = await opOnEngine(actionEngine, AIEMOpType.resize, undefined, undefined);
        setEngineResize(null);
        if (res) {
          addToast({
            messageBarType: MessageBarType.success,
            message: `Engine ${String(actionEngine.displayName)} is resizing. This process may take some time.`,
          });
        }
      },
      [actionEngine, addToast, opOnEngine]
    ),
    onConfirmMigrateCreatorDialog = useCallback(
      async (creatorId?: string) => {
        if (!actionEngine?.op) {
          actionEngineErrorMessage('Change creator');
          return;
        }
        actionEngine.newCreator = creatorId;
        const res = await opOnEngine(actionEngine, AIEMOpType.migrateCreator, undefined, undefined);
        setMigrateCreator(null);
        if (res) successToast(String(actionEngine.displayName), ' creator was changed');
      },
      [actionEngine, addToast, opOnEngine]
    ),
    deleteEngine = useCallback(
      (e: AIEngine) => {
        const op = AIEMOpType.delete;
        setBulkActionEngine([]);
        setSelectedEngineIds([]);
        setActionEngine({ ...e, op });
        setDialog({ op });
      },
      [setDialog]
    ),
    upgradeEngine = useCallback(
      (e: AIEngine) => {
        const op = AIEMOpType.upgrade;
        setDeprecatedEngine(null);
        setActionEngine({ ...e, op });
        setUpgrade(op);
      },
      [setDialog]
    ),
    showResizeModal = useCallback(
      (e: AIEngine) => {
        const op = AIEMOpType.resize;
        setActionEngine({ ...e, op });
        setEngineResize(op);
      },
      [setEngineResize]
    ),
    migrateEngineCreator = useCallback(
      (e: AIEngine) => {
        const op = AIEMOpType.migrateCreator;
        setActionEngine({ ...e, op });
        setMigrateCreator(op);
      },
      [setDialog]
    ),
    resumeEngine = useCallback(
      async (engine: AIEngine) => {
        setBulkActionEngine([]);
        setSelectedEngineIds([]);
        setDeprecatedEngine(null);
        let res;
        try {
          res = await opOnEngine(engine, AIEMOpType.resume, undefined, undefined);
        } catch (error: any) {
          console.error(error);
          if (error.code === 9) void editEngine(engine);
          return;
        }
        if (res) successToast(String(engine.displayName), 'is resuming');
        engines.refetch();
      },
      [engines.refetch, successToast, opOnEngine]
    ),
    checkForResumeEngine = useCallback((engine: AIEngine) => {
      if (engine.deprecatedVersion) {
        setDeprecatedEngine(engine);
      } else {
        resumeEngine(engine);
      }
    }, []),
    pauseEngine = useCallback(
      (e: AIEngine) => {
        setBulkActionEngine([]);
        setSelectedEngineIds([]);
        const op = AIEMOpType.pause;
        setActionEngine({ ...e, op });
        setDialog({ op });
      },
      [setDialog, setActionEngine]
    ),
    terminateEngine = useCallback(
      (e: AIEngine) => {
        setBulkActionEngine([]);
        setSelectedEngineIds([]);
        const op = AIEMOpType.terminate;
        setActionEngine({ ...e, op });
        setDialog({ op });
      },
      [setDialog, setActionEngine]
    ),
    bulkAction = useCallback(
      async (op: AIEMOpType.delete | AIEMOpType.pause | AIEMOpType.resume | AIEMOpType.terminate) => {
        const selectedEngines =
          (engines.data?.filter(({ uid }) => uid && selectedEngineIds.includes(uid)) as AIEngine[]) || [];
        setBulkActionEngine(selectedEngines);
        setDialog({ op });
      },
      [selectedEngineIds]
    ),
    onDismissDialog = useCallback(() => {
      setDialog(null);
    }, [setDialog]),
    onDismissUpgradeDialog = useCallback(() => {
      setUpgrade(null);
    }, [setUpgrade]),
    onDismissResizeDialog = useCallback(() => {
      setEngineResize(null);
    }, [setEngineResize]),
    onDismissMigrateCreatorDialog = useCallback(() => {
      setMigrateCreator(null);
    }, [setMigrateCreator]),
    onCreateClick = () => {
      setPanel({ op: AIEMOpType.create, isOpen: true });
    },
    dismissPanel = useCallback(() => {
      setPanel({ op: null, isOpen: false, engine: undefined });
    }, []),
    onSavePanel = useCallback(async () => {
      dismissPanel();
      engines.refetch();
    }, [actionEngine, engines.refetch, panel.op, successToast, opOnEngine]);

  const debouncedSetSearchParam = useDebouncedCallback(engines.setSearchTerm, 300);
  const [searchKey, setSearchKey] = useState(engines.params.searchTerm);
  const onChangeSearchInput = useCallback(
    (_, value) => {
      const newValue = value || '';
      setSearchKey(newValue);
      debouncedSetSearchParam(newValue);
    },
    [debouncedSetSearchParam, setSearchKey]
  );
  const onResetFilters = useCallback(() => {
    engines.setParams(searchKey, [], {});
  }, [engines, searchKey]);

  const activeFilters = useMemo(() => {
    function deriveVersionFilters(engineType: Engine_Type, label: string) {
      const versionMap = engines.params?.versions?.[engineType];
      if (!versionMap) return [];
      const filteredVersions = Object.keys(versionMap).filter((version) => versionMap[version]);
      return filteredVersions.map((version) => ({
        key: `${label}-${version}`,
        label: `${label} ${version}`,
        data: {
          type: EngineFilterType.Version,
          engineType,
          version,
        },
      }));
    }

    const stateFilters = engines.params?.states?.map((state) => {
      const title = EngineStateMap.get(state as EngineState)?.title;
      return {
        key: `state-${state}`,
        label: title || 'Unknown state',
        data: {
          type: EngineFilterType.State,
          state,
        },
      };
    });

    return [
      ...stateFilters,
      ...deriveVersionFilters(Engine_Type.DRIVERLESS_AI, 'DAI'),
      ...deriveVersionFilters(Engine_Type.H2O, 'H2O'),
    ];
  }, [engines.params]);
  useEffect(() => {
    if (engines.isError && engines.error) {
      lastErrorRef.current = engines.error;
    } else if (!engines.loading && engines.data !== undefined) {
      lastErrorRef.current = null;
    }
  }, [engines.isError, engines.error, engines.loading, engines.data]);

  const error = useMemo(() => {
    const currentError = engines.isError && engines.error ? engines.error : lastErrorRef.current;
    if (currentError) return formatError(currentError);
    if (!permissionsLoading) {
      if (!canListSome && !admin) return 'You are not authorized to access AI Engines in this Workspace.';

      // API returns false for every permission when fetching for all workspaces ("workspaces/-")
      // TODO: Uncomment when fixed
      // if (!canListAll && admin) return 'You are not authorized to access AI Engines.';
    }
    return undefined;
  }, [
    engines.isError,
    engines.error,
    engines.loading,
    engines.data,
    permissionsLoading,
    canListSome,
    canListAll,
    admin,
  ]);

  const bulkActions = [
    ...(!selectedEnginesContainsDeleted
      ? [
          {
            'data-test': 'bulk-resume-engine-button',
            key: 'bulk-resume-engine',
            text: 'Resume',
            onClick: !canResumeSome
              ? () => null
              : () => {
                  bulkAction(AIEMOpType.resume);
                },
            disabled: !canResumeSome,
          },
        ]
      : []),
    {
      'data-test': 'bulk-pause-engine-button',
      key: 'bulk-pause-engine',
      text: 'Pause',
      onClick: !canPauseSome
        ? () => null
        : () => {
            bulkAction(AIEMOpType.pause);
          },
      disabled: !canPauseSome,
    },
    {
      'data-test': 'bulk-terminate-engine-button',
      key: 'bulk-terminate-engine',
      text: 'Terminate',
      onClick() {
        bulkAction(AIEMOpType.terminate);
      },
      disabled: !canH2OTerminate,
    },
    {
      'data-test': 'bulk-delete-engine-button',
      key: 'bulk-delete-engine',
      text: 'Delete',
      dangerous: true,
      onClick: !canDeleteSome
        ? () => null
        : () => {
            bulkAction(AIEMOpType.delete);
          },
      disabled: !canDeleteSome,
    },
  ];

  const availableEnginesCount = engines.data?.length || 0;

  useEffect(() => {
    if (canFetchEngines) engines.refetch();
  }, [canFetchEngines, engines.refetch]);

  return (
    <>
      <FilterPanel isOpen={filterPanelOpen} onDismiss={dismissFilterPanel}>
        <FilterPanelInput>
          <EngineVersionFilter />
        </FilterPanelInput>
        <FilterPanelInput>
          <EngineStateFilter onChangeStates={engines.setStates} />
        </FilterPanelInput>
      </FilterPanel>
      {panel.isOpen && (
        <AIEMPanelWrapper
          onDismiss={dismissPanel}
          op={panel.op || AIEMOpType.create}
          engine={panel.engine!}
          onSave={onSavePanel}
        />
      )}
      <ListPage
        actions={bulkActions}
        activeFilters={activeFilters}
        copy={{
          title: props.title,
          subtitle: (() => {
            const isLoading = (permissionsLoading && engines.loading) || !engines.data;

            if (isLoading) return 'Loading engines...';
            if (availableEnginesCount === 0) return 'There are no AI engines';

            const isPlural = availableEnginesCount > 1;
            const verb = admin ? (isPlural ? 'are' : 'is') : 'have';
            const subject = admin ? 'There' : 'You';
            const engineText = `AI engine${isPlural ? 's' : ''}`;
            const workspaceText =
              !admin && activeWorkspace?.displayName ? ` in the "${activeWorkspace.displayName}" workspace` : '';

            return `${subject} ${verb} ${availableEnginesCount} ${engineText}${workspaceText}`;
          })(),
          loadingMessage: 'Loading AI Engines...',
          noDataTitle: 'No engines found',
          noDataMessage: 'Click on Create AI Engine to add your first AI Engine',
          noFilterResultsMessage: 'No AI engines match these filters',
          searchPlaceholder: 'Search by Engine Display Name or ID',
        }}
        disableActions={selectedEngineIds.length === 0}
        loading={(permissionsLoading && engines.loading) || (!engines.data && !error)}
        primaryButtonProps={{
          text: 'Create AI Engine',
          onClick: onCreateClick,
          disabled: !canCreateSome,
          tooltip: restrictionTooltipMessage(!canCreateSome),
        }}
        showFilterButton
        showNoDataPage={Boolean(engines.data?.length === 0 && !searchKey && !error)}
        showNoResults={Boolean(engines.data?.length === 0 && searchKey && !error)}
        searchText={searchKey}
        onChangeSearchText={onChangeSearchInput}
        onOpenFilterPanel={openFilterPanel}
        onRemoveFilter={({ data }) => {
          if (data?.type === EngineFilterType.Version) {
            const engineType = data?.engineType;
            const versionToRemove = data?.version;
            if (engineType && versionToRemove) {
              const nextVersionParams = { ...(engines.params?.versions?.[engineType] || {}) };
              delete nextVersionParams[versionToRemove];
              engines.setTypeVersions({
                ...engines.params?.versions,
                [engineType]: nextVersionParams,
              });
            }
          }
          if (data?.type === EngineFilterType.State) {
            const stateToRemove = data?.state;
            if (stateToRemove) {
              engines.setStates(engines.params?.states?.filter((s) => s !== stateToRemove));
            }
          }
        }}
        onResetFilters={onResetFilters}
        error={error}
      >
        {platformDiscovery?.aiEngineManagerApiUrl ? (
          <>
            <EngineList
              engines={engines.data}
              viewEngine={viewEngine}
              editEngine={editEngine}
              resumeEngine={checkForResumeEngine}
              pauseEngine={pauseEngine}
              terminateEngine={terminateEngine}
              deleteEngine={deleteEngine}
              upgradeEngine={upgradeEngine}
              showResizeModal={showResizeModal}
              migrateCreator={migrateEngineCreator}
              admin={admin}
              readLogs={readLogs}
              openLegacyEngineLogs={openLegacyEngineLogs}
              selectedEngineIds={selectedEngineIds}
              setSelectedEngineIds={setSelectedEngineIds}
            />
            <AIEMConfirmDialog
              hidden={!dialog}
              engines={bulkActionEngine.length > 0 ? bulkActionEngine : actionEngine ? [actionEngine] : []}
              op={dialog?.op || AIEMOpType.pause}
              onDismiss={onDismissDialog}
              onConfirm={bulkActionEngine.length > 0 ? onConfirmBulkDialog : onConfirmDialog}
            />
            <AIEMUpgradeDialog
              hidden={!upgrade}
              engine={actionEngine}
              onDismiss={onDismissUpgradeDialog}
              onConfirm={onConfirmUpgradeDialog}
            />
            <AIEMMigrateCreator
              hidden={!migrateCreator}
              engine={actionEngine}
              onConfirm={onConfirmMigrateCreatorDialog}
              onDismiss={onDismissMigrateCreatorDialog}
            />
            <EngineResizeDialog
              engine={actionEngine}
              admin={admin}
              hidden={!engineResize}
              onConfirm={onConfirmResizeDialog}
              onDismiss={onDismissResizeDialog}
            />

            <BaseDialog
              hidden={!deprecatedEngine}
              title={'The current engine version is deprecated'}
              content={<p>It is recommended to first upgrade the engine before resuming.</p>}
              onDismiss={() => setDeprecatedEngine(null)}
              footer={
                <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }}>
                  <Button
                    styles={buttonStylesPrimary}
                    onClick={() => deprecatedEngine && upgradeEngine(deprecatedEngine)}
                    text="Upgrade"
                  />
                  <Button
                    styles={buttonStylesPrimary}
                    onClick={() => deprecatedEngine && resumeEngine(deprecatedEngine)}
                    text="Resume without upgrading"
                  />
                  <Button onClick={() => setDeprecatedEngine(null)} text="Cancel" />
                </Stack>
              }
            />
          </>
        ) : (
          <Stack styles={stackStylesPage}>
            <h2 style={{ textAlign: 'center' }}>Sorry, AI Engine Manager has not been enabled for this environment.</h2>
          </Stack>
        )}
      </ListPage>
    </>
  );
}

export default AIEnginesPage;
